(() => {
  'use strict';
  if (window.KMMotion) return;
  const html = document.documentElement;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const hover = matchMedia('(hover: hover) and (pointer: fine)');
  const controllers = new Set();
  const revealSeen = new WeakSet();
  const liveAnimations = new Set();
  let keyboard = false, sequence = 0;
  const motionOn = () => !reduced.matches && html.dataset.kmMotion !== 'off';
  const faqDuration = () => motionOn() ? Number(html.dataset.kmFaqDuration || 260) : 0;
  const all = (scope, selector) => [...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector)];
  const faqSelector = '.km-faq-item, .km-home-faq details, .km-faq-section details';
  const revealSelector = '#MainContent .section-head, #MainContent .product-card, #MainContent .cat-card, #MainContent .km-category-card, #MainContent .why-card, #MainContent .price-card, #MainContent .km-watch-card, #MainContent .km-story-card, #MainContent .km-finder-shell, #MainContent .km-faq-list, #MainContent .km-home-faq';

  document.addEventListener('pointerdown', () => { keyboard = false; }, true);
  document.addEventListener('keydown', event => {
    if (['Tab', 'Enter', ' ', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) keyboard = true;
    if (event.key === 'Escape') controllers.forEach(controller => {
      if (controller.expanded && (controller.hovered || controller.item.contains(document.activeElement))) controller.dismiss();
    });
  }, true);

  function enhanceFAQ(item) {
    if (item.dataset.kmFaqReady) return;
    const summary = item.querySelector(':scope > summary');
    if (!summary) return;
    item.dataset.kmFaqReady = 'true';
    item.classList.add('km-faq-enhanced');
    const body = document.createElement('div');
    body.className = 'km-faq-body';
    [...item.childNodes].filter(node => node !== summary).forEach(node => body.append(node));
    item.append(body);
    const number = ++sequence;
    summary.id ||= 'km-faq-summary-' + number;
    body.id = 'km-faq-answer-' + number;
    summary.setAttribute('aria-controls', body.id);
    body.setAttribute('aria-labelledby', summary.id);
    const events = new AbortController();
    let expanded = item.open, hovered = false, focusedByKeyboard = false, dismissed = false;
    let animation = null, closeTimer;
    const group = item.closest('.km-faq-list, .km-home-faq') || item.parentElement;

    function attributes(value) {
      item.dataset.kmFaqExpanded = String(value);
      summary.setAttribute('aria-expanded', String(value));
      body.setAttribute('aria-hidden', String(!value));
      body.inert = !value;
    }
    function finish(value) {
      item.open = value;
      item.style.removeProperty('height');
      item.style.removeProperty('overflow');
    }
    function setExpanded(value) {
      clearTimeout(closeTimer);
      if (value === expanded) return;
      const start = item.getBoundingClientRect().height;
      if (animation) { animation.cancel(); animation = null; }
      expanded = value;
      if (value && html.dataset.kmFaqSingle !== 'false') controllers.forEach(other => {
        if (other.item !== item && other.group === group && !other.item.contains(document.activeElement)) other.close();
      });
      if (!value && body.contains(document.activeElement)) {
        dismissed = true;
        summary.focus({ preventScroll: true });
      }
      attributes(value);
      item.open = true;
      item.style.height = 'auto';
      const style = getComputedStyle(item);
      const borders = parseFloat(style.borderTopWidth || 0) + parseFloat(style.borderBottomWidth || 0);
      const end = value ? item.getBoundingClientRect().height : summary.getBoundingClientRect().height + borders;
      const duration = faqDuration();
      if (!duration || !item.animate || Math.abs(end - start) < 1) { finish(value); return; }
      item.style.overflow = 'hidden';
      const current = item.animate([{ height: start + 'px' }, { height: end + 'px' }], { duration, easing: 'cubic-bezier(.22,.7,.22,1)' });
      animation = current;
      current.onfinish = () => {
        if (animation !== current) return;
        animation = null;
        finish(expanded);
      };
    }
    const controller = {
      item, group,
      get expanded() { return expanded; },
      get hovered() { return hovered; },
      close() { setExpanded(false); },
      dismiss() { dismissed = true; setExpanded(false); },
      settle() { if (animation) { animation.cancel(); animation = null; finish(expanded); } },
      destroy() { clearTimeout(closeTimer); this.settle(); events.abort(); controllers.delete(this); }
    };
    controllers.add(controller);
    attributes(expanded);
    const canHover = event => item.closest('[data-product-faq-hover]')?.dataset.productFaqHover !== 'false' && html.dataset.kmFaqHover !== 'false' && hover.matches && event.pointerType === 'mouse';
    item.addEventListener('pointerenter', event => {
      if (!canHover(event)) return;
      hovered = true; clearTimeout(closeTimer);
    }, { signal: events.signal });
    summary.addEventListener('pointerenter', event => {
      if (!canHover(event)) return;
      hovered = true; dismissed = false; setExpanded(true);
    }, { signal: events.signal });
    item.addEventListener('pointerleave', event => {
      if (!canHover(event)) return;
      hovered = false;
      closeTimer = setTimeout(() => { if (!focusedByKeyboard) setExpanded(false); }, 140);
    }, { signal: events.signal });
    item.addEventListener('focusin', event => {
      clearTimeout(closeTimer);
      if (!item.contains(event.relatedTarget)) dismissed = false;
      focusedByKeyboard = keyboard;
      if (keyboard && !dismissed) setExpanded(true);
    }, { signal: events.signal });
    item.addEventListener('focusout', event => {
      if (item.contains(event.relatedTarget)) return;
      focusedByKeyboard = false;
      if (!hovered) closeTimer = setTimeout(() => setExpanded(false), 140);
    }, { signal: events.signal });
    summary.addEventListener('click', event => {
      if (event.target.closest('a, button, input, select')) return;
      event.preventDefault();
      dismissed = expanded;
      setExpanded(!expanded);
    }, { signal: events.signal });
    // Keep native open/close changes from other integrations in sync.
    item.addEventListener('toggle', () => {
      if (animation || item.open === expanded) return;
      expanded = item.open; attributes(expanded);
    }, { signal: events.signal });
    if (location.hash === '#' + item.id && item.id) setExpanded(true);
  }

  function reveal(element) {
    if (revealSeen.has(element)) return;
    revealSeen.add(element);
    if (!motionOn() || !element.animate || element.closest('[hidden]')) return;
    const siblings = element.parentElement ? [...element.parentElement.children] : [];
    const delay = Math.min(Math.max(siblings.indexOf(element), 0), 4) * 45;
    const lively = html.dataset.kmMotion === 'lively';
    const animation = element.animate([
      { opacity: .25, transform: 'translateY(' + (lively ? 22 : 12) + 'px)' },
      { opacity: 1, transform: 'translateY(0)' }
    ], { duration: lively ? 600 : 440, delay, easing: 'cubic-bezier(.2,.7,.2,1)' });
    liveAnimations.add(animation);
    const clear = () => liveAnimations.delete(animation);
    animation.onfinish = clear; animation.oncancel = clear;
  }
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) { observer.unobserve(entry.target); reveal(entry.target); }
    });
  }, { threshold: .08, rootMargin: '0px 0px -16px 0px' }) : null;
  const observed = new WeakSet();
  function init(scope) {
    all(scope, faqSelector).forEach(enhanceFAQ);
    if (!observer || !motionOn()) return;
    all(scope, revealSelector).forEach(element => {
      if (!observed.has(element)) { observed.add(element); observer.observe(element); }
    });
  }
  function start() {
    init(document);
    const main = document.getElementById('MainContent');
    if (main && 'MutationObserver' in window) new MutationObserver(records => {
      records.forEach(record => record.addedNodes.forEach(node => { if (node.nodeType === 1) init(node); }));
      controllers.forEach(controller => { if (!controller.item.isConnected) controller.destroy(); });
    }).observe(main, { childList: true, subtree: true });
  }
  reduced.addEventListener('change', () => {
    if (reduced.matches) {
      liveAnimations.forEach(animation => animation.cancel());
      controllers.forEach(controller => controller.settle());
    }
  });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    controllers.forEach(controller => { if (event.target.contains(controller.item)) controller.destroy(); });
  });
  document.addEventListener('shopify:block:select', event => {
    const item = event.target.matches?.(faqSelector) ? event.target : event.target.querySelector?.(faqSelector);
    if (!item) return;
    init(item);
    item.open = true;
  });
  window.KMMotion = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
